/**
 * What a traveller without PLUS sees where the destination health check would be.
 *
 * Planning and managing trips is free (REQ-002 §5, REQ-009 "Basic travel
 * planning"); the health requirements behind the check are sold with PLUS
 * (REQ-009 "Travel health requirements") and the backend answers 402 without
 * it. This panel is the honest version of that: the check is optional, here is
 * what PLUS adds, and the way on without it is as visible as the upgrade.
 *
 * The benefits are the PLUS rows of REQ-009's feature matrix, which the plans
 * page lists too - nothing that plan does not deliver, and no promise about
 * the traveller's health.
 */

import { useI18nStore } from "@/stores/i18nStore";
import { Check, ShieldCheck, Sparkles } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

const PLANS_PATH = "/traveler/subscription/plans";

interface TripHealthUpsellProps {
  country: string;
  /** The wizard passes this to offer "Continue without health check". */
  onSkip?: () => void;
  /** The wizard keeps a draft; the trip page has nothing to come back to. */
  showDraftNote?: boolean;
}

export const TripHealthUpsell: React.FC<TripHealthUpsellProps> = ({
  country,
  onSkip,
  showDraftNote = false,
}) => {
  const { t } = useI18nStore();

  const benefits = [
    t.tripHealthBenefitVaccinations,
    t.tripHealthBenefitWarnings,
    t.tripHealthBenefitTracking,
    t.tripHealthBenefitDocuments,
    t.tripHealthBenefitOffline,
  ];

  const destination = country || t.destination;

  return (
    <section
      aria-labelledby="trip-health-upsell-title"
      className="space-y-4 rounded-xl border border-indigo-200 bg-indigo-50/60 p-5"
      data-testid="trip-health-upsell"
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-indigo-600" />
        <div>
          <h3
            id="trip-health-upsell-title"
            className="text-base font-semibold text-gray-900"
          >
            {t.tripHealthUpsellTitle}
          </h3>
          <p className="mt-1 text-sm text-gray-700">
            {t.tripHealthUpsellIntro.replace("{country}", destination)}
          </p>
        </div>
      </div>

      <ul className="space-y-2 pl-9">
        {benefits.map((benefit) => (
          <li key={benefit} className="flex items-start gap-2 text-sm">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
            <span className="text-gray-800">{benefit}</span>
          </li>
        ))}
      </ul>

      <p className="flex items-start gap-2 pl-9 text-sm text-gray-700">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
        <span>{t.tripHealthSingleTrip}</span>
      </p>

      <div className="flex flex-col gap-3 pl-9 sm:flex-row sm:items-center">
        <Link
          to={PLANS_PATH}
          className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700"
        >
          {t.tripHealthUpgradeCta}
        </Link>
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            {t.tripHealthSkipCta}
          </button>
        )}
      </div>

      {showDraftNote && (
        <p className="pl-9 text-xs text-gray-500">{t.tripHealthDraftKept}</p>
      )}
    </section>
  );
};

export default TripHealthUpsell;
